import { templateManifestSchema } from "@hightouch/lib/ad-studio/templates/manifest-schema";
import type { TemplateManifest } from "@hightouch/lib/ad-studio/templates/types";
import {
  type EditorSlotValue,
  exampleSlotValue,
} from "@hightouch/lib/ad-studio/templates/editor-slots";
import {
  AD_STUDIO_TEMPLATE_IMPORT_SOURCE,
  type AdStudioTemplateImportSource,
} from "@hightouch/lib/llm/shared";

import { matchesTemplateSearch } from "src/pages/ad-studio/templates/matches-template-search";

import type {
  AdStudioTemplateQuery,
  AdStudioTemplatesQuery,
  AgentSessionsQuery,
} from "src/graphql";
import {
  AgenticSettingsEntityType,
  type UpdateAgenticSettingsEntityInput,
} from "src/graphql/types";

type GalleryEntity =
  AdStudioTemplatesQuery["getCurrentAgenticSettingsEntities"][number];
type DetailEntity =
  AdStudioTemplateQuery["getCurrentAgenticSettingsEntities"][number];

export type AdStudioTemplateCard = Omit<GalleryEntity, "content"> & {
  content: Extract<
    GalleryEntity["content"],
    { __typename: "AgenticSettingsAdTemplateContent" }
  >;
};

export type AdStudioTemplateDraft = Omit<GalleryEntity, "content"> & {
  content: Extract<
    GalleryEntity["content"],
    { __typename: "AgenticSettingsAdTemplateDraftContent" }
  >;
};

export function isAdStudioTemplateDraft(
  entity: GalleryEntity,
): entity is AdStudioTemplateDraft {
  return entity.content.__typename === "AgenticSettingsAdTemplateDraftContent";
}

export type AdStudioTemplateDetail = Omit<DetailEntity, "content"> & {
  content: Extract<
    DetailEntity["content"],
    { __typename: "AgenticSettingsAdTemplateContent" }
  >;
};

export type AdStudioTemplateImportSession =
  AgentSessionsQuery["llm_chat_sessions"][number];

export type AdStudioTemplateImportStatus =
  | "importing"
  | "finalizing"
  | "needs-attention";

export type TemplateImportDrawerStatus =
  | AdStudioTemplateImportStatus
  | "failed";

export const AD_STUDIO_TEMPLATES_PATH = "/ad-studio/templates";

const TEMPLATE_FINALIZATION_GRACE_MS = 30_000;

export function templateImportCompletionPath(templateId: string): string {
  return `${AD_STUDIO_TEMPLATES_PATH}/${templateId}`;
}

export function getAdStudioTemplateImportStatus(
  session: AdStudioTemplateImportSession | undefined,
  draftStatus: AdStudioTemplateDraft["content"]["status"] | undefined,
  now = Date.now(),
): AdStudioTemplateImportStatus {
  if (session?.in_progress_interaction.aggregate?.count) {
    return "importing";
  }

  if (draftStatus === "needs-attention" || draftStatus === "failed") {
    return "needs-attention";
  }
  if (!session) {
    return "importing";
  }
  const latestInteraction = session.latest_root_interaction[0];
  if (
    latestInteraction?.status === "complete" &&
    now - new Date(latestInteraction.updated_at).getTime() <
      TEMPLATE_FINALIZATION_GRACE_MS
  ) {
    return "finalizing";
  }

  return "needs-attention";
}

export function sortAdStudioTemplateImports(
  sessions: AdStudioTemplateImportSession[],
): AdStudioTemplateImportSession[] {
  return sessions
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
}

export function isAdStudioTemplateCard(
  entity: GalleryEntity,
): entity is AdStudioTemplateCard {
  return entity.content.__typename === "AgenticSettingsAdTemplateContent";
}

export function isPublishedAdStudioTemplate(
  entity: GalleryEntity,
): entity is AdStudioTemplateCard {
  return isAdStudioTemplateCard(entity) && entity.content.status !== "draft";
}

export function isAdStudioTemplateDetail(
  entity: DetailEntity,
): entity is AdStudioTemplateDetail {
  return entity.content.__typename === "AgenticSettingsAdTemplateContent";
}

export function parseTemplateManifest(value: unknown): TemplateManifest | null {
  const result = templateManifestSchema.safeParse(value);
  return result.success ? (result.data as TemplateManifest) : null;
}

export function filterAdStudioTemplates<
  T extends Pick<
    AdStudioTemplateCard,
    "name" | "description" | "brandProfileAssignments"
  >,
>(templates: T[], search: string, brandProfileId: string | null = null): T[] {
  return templates.filter((template) => {
    const assignments = template.brandProfileAssignments.filter(
      (assignment) => !assignment.isDeleted,
    );
    const matchesBrand =
      !brandProfileId ||
      assignments.length === 0 ||
      assignments.some((assignment) => assignment.id === brandProfileId);
    return (
      matchesBrand &&
      (matchesTemplateSearch(template.name, search) ||
        matchesTemplateSearch(template.description ?? "", search))
    );
  });
}

export function filterAdStudioTemplateImports(
  sessions: AdStudioTemplateImportSession[],
  search: string,
  brandProfileId: string | null,
): AdStudioTemplateImportSession[] {
  return sessions.filter((session) => {
    const context = session.context as {
      context?: { brandProfileId?: string };
    } | null;
    return (
      (!brandProfileId ||
        context?.context?.brandProfileId === brandProfileId) &&
      matchesTemplateSearch(session.name ?? "Template import", search)
    );
  });
}

export function importSourceLabel(
  source: string,
): "Ad" | "Figma" | "HTML" | "Image" | "PSD" | "Unknown" {
  switch (source as AdStudioTemplateImportSource) {
    case AD_STUDIO_TEMPLATE_IMPORT_SOURCE.Ad:
      return "Ad";
    case AD_STUDIO_TEMPLATE_IMPORT_SOURCE.Figma:
      return "Figma";
    case AD_STUDIO_TEMPLATE_IMPORT_SOURCE.Html:
      return "HTML";
    case AD_STUDIO_TEMPLATE_IMPORT_SOURCE.Image:
      return "Image";
    case AD_STUDIO_TEMPLATE_IMPORT_SOURCE.Psd:
      return "PSD";
    default:
      return "Unknown";
  }
}

export function parseTemplateStringMap(value: unknown): Map<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return new Map();
  }
  return new Map(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function parseTemplateAssets(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function buildAdTemplateExampleValues({
  manifest,
  originalSampleValues,
  assets,
  apiBase,
}: {
  manifest: TemplateManifest;
  originalSampleValues: Map<string, string>;
  assets: Record<string, string>;
  apiBase?: string;
}): Map<string, EditorSlotValue> {
  const assetNameByStorageKey = new Map(
    Object.entries(assets).map(([name, storageKey]) => [storageKey, name]),
  );
  return new Map(
    manifest.slots.map((slot): [string, EditorSlotValue] => {
      const fallback = exampleSlotValue(slot, null);
      const original = originalSampleValues.get(slot.id);
      if (original === undefined) return [slot.id, fallback];
      if (fallback.kind === "text") {
        return [slot.id, { kind: "text", text: original }];
      }
      const assetName =
        assetNameByStorageKey.get(original) ??
        (Object.hasOwn(assets, original) ? original : null);
      if (!assetName) return [slot.id, fallback];
      const url = apiBase
        ? `${apiBase}/agents/files?${new URLSearchParams({
            key: assets[assetName] ?? original,
          }).toString()}`
        : `ad:${encodeURIComponent(assetName)}`;
      return [slot.id, { kind: "image", url }];
    }),
  );
}

export function buildAdTemplateUpdateInput(
  template: AdStudioTemplateDetail,
  contentUpdates: Partial<
    Pick<
      AdStudioTemplateDetail["content"],
      "usageGuidance" | "editorHtml" | "manifest"
    >
  >,
  expectedVersionId: string | null = template.latestVersionId,
): UpdateAgenticSettingsEntityInput {
  const { content } = template;
  return {
    id: template.id,
    name: template.name,
    description: template.description ?? null,
    isActive: template.isActive,
    type: AgenticSettingsEntityType.AdTemplate,
    ...(expectedVersionId ? { expectedVersionId } : {}),
    adTemplate: {
      usageGuidance: contentUpdates.usageGuidance ?? content.usageGuidance,
      editorHtml: contentUpdates.editorHtml ?? content.editorHtml,
      manifest: contentUpdates.manifest ?? content.manifest,
    },
  };
}
